import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { Box, Button, Icon, Paper, Tooltip, Typography } from '@mui/material'
import CheckboxInput from './CheckboxInput'
import theme from '../../theme/theme'
import { useTranslation } from 'react-i18next'
import { ViewColumnOutlined } from '@mui/icons-material'
import { ColumnOrderListType } from '../../redux/slices/entitySlice'

interface ColumnOrderListProps {
    list: ColumnOrderListType[]
    handleUpdateList: (list: ColumnOrderListType[]) => void
    handleOnSaveList: (list: ColumnOrderListType[]) => void
    handleOnResetList: () => void
    isRightSide?: boolean
}

const ColumnOrderList: React.FC<ColumnOrderListProps> = ({
    list,
    handleUpdateList,
    handleOnSaveList,
    handleOnResetList,
    isRightSide = false,
}: ColumnOrderListProps) => {
    const { t } = useTranslation()
    const filterDropdownRef = useRef<HTMLDivElement>(null)

    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false)

    const [ColumnOrderList, setColumnOrderList] = useState<
        ColumnOrderListType[]
    >([])

    const [draggedItem, setDraggedItem] = useState<string | null>(null)
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        setColumnOrderList(list)
    }, [list])

    // Optimize drag handlers with useCallback
    const handleDragStart = useCallback(
        (e: React.DragEvent, itemId: string) => {
            setDraggedItem(itemId)
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/html', itemId)

            // Add visual feedback to dragged element
            if (e.currentTarget instanceof HTMLElement) {
                e.currentTarget.style.opacity = '0.5'
            }
        },
        [],
    )

    const handleDragEnd = useCallback((e: React.DragEvent) => {
        setDraggedItem(null)
        setDragOverIndex(null)

        // Reset visual feedback
        if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = '1'
        }
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'

        // Only update if different to reduce re-renders
        setDragOverIndex((prevIndex) =>
            prevIndex !== index ? index : prevIndex,
        )
    }, [])

    const handleDragLeave = useCallback(() => {
        // Add small delay to prevent flickering
        const timeoutId = setTimeout(() => setDragOverIndex(null), 5)
        return () => clearTimeout(timeoutId)
    }, [])

    const handleDrop = useCallback(
        (e: React.DragEvent, dropIndex: number) => {
            e.preventDefault()

            if (!draggedItem) return

            const draggedIndex = ColumnOrderList.findIndex(
                (item) => item.id === draggedItem,
            )
            if (draggedIndex === -1 || draggedIndex === dropIndex) return

            // Create new array with reordered items
            const newColumnOrderList = [...ColumnOrderList]
            const [draggedElement] = newColumnOrderList.splice(draggedIndex, 1)
            newColumnOrderList.splice(dropIndex, 0, draggedElement)

            // Update states immediately for instant feedback
            setColumnOrderList(newColumnOrderList)
            handleUpdateList(newColumnOrderList)
            setDraggedItem(null)
            setDragOverIndex(null)
        },
        [draggedItem, ColumnOrderList, handleUpdateList],
    )

    const handleCheckboxChange = useCallback(
        (id: string) => {
            const updatedList = ColumnOrderList.map((item) => ({
                ...item,
                selected: item.id === id ? !item.selected : item.selected,
            }))
            setColumnOrderList(updatedList)
            handleUpdateList(updatedList)
        },
        [ColumnOrderList, handleUpdateList],
    )

    const renderDraggableItems = useMemo(
        () => (
            <Box sx={{ maxHeight: '300px', overflowY: 'auto', padding: '8px' }}>
                {ColumnOrderList.map((item, index) => (
                    <Box
                        key={item.id}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px 4px',
                            backgroundColor:
                                dragOverIndex === index
                                    ? '#e3f2fd'
                                    : 'transparent',
                            border:
                                dragOverIndex === index
                                    ? '2px dashed #1976d2'
                                    : '2px solid transparent',
                            borderRadius: '4px',
                            marginBottom: '4px',
                            cursor: 'grab',
                            // Remove transition for better drag performance
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                            '&:active': {
                                cursor: 'grabbing',
                            },
                        }}
                    >
                        {/* Drag handle */}
                        <Box
                            sx={{
                                marginRight: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#666',
                                fontSize: '14px',
                                cursor: 'grab',
                                userSelect: 'none',
                                '&:active': {
                                    cursor: 'grabbing',
                                },
                            }}
                        >
                            ⋮⋮
                        </Box>
                        {/* Checkbox */}
                        <Box sx={{ flexGrow: 1 }}>
                            <CheckboxInput
                                id={item.id}
                                label={t(item.name)}
                                value={item.selected}
                                onChange={() => handleCheckboxChange(item.id)}
                                labelStyels={{
                                    fontSize: '14px',
                                    opacity: 1,
                                    fontWeight: '400',
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
        ),
        [
            ColumnOrderList,
            dragOverIndex,
            handleDragStart,
            handleDragEnd,
            handleDragOver,
            handleDragLeave,
            handleDrop,
            handleCheckboxChange,
            t,
        ],
    )

    const handleClickOutside = (event: any) => {
        const container = filterDropdownRef.current ?? document.body // Fallback to body
        if (container.contains(event.target)) {
            // nothing
        } else {
            setIsFilterOpen(false)
        }
    }

    // handle re-order save
    const handleSave = useCallback(() => {
        if (handleOnSaveList) {
            handleUpdateList(ColumnOrderList)
            handleOnSaveList(ColumnOrderList)
        }
        setIsFilterOpen(false)
    }, [handleOnSaveList, handleUpdateList, ColumnOrderList])

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
            }}
            ref={filterDropdownRef}
        >
            <Tooltip title={t('columnsOrder')} placement="top">
                <Icon
                    id="filter-list-icon-column-filter" //for automation testing added this Id(initial development Id)(same id in list and preview pages)
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    sx={{
                        width: 'auto',
                        height: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <ViewColumnOutlined
                        sx={{
                            color: theme.palette.primary.main,
                            fontSize: 28,
                        }}
                    />
                </Icon>
            </Tooltip>
            {isFilterOpen && (
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        zIndex: 1000,
                        minWidth: '300px',
                        maxWidth: '350px',
                        maxHeight: '430px',
                        boxShadow: 3,
                        borderRadius: '8px',
                        overflow: 'auto',
                        marginTop: '8px',
                        ...(isRightSide
                            ? {
                                  left: 0,
                                  right: 'auto',
                              }
                            : {
                                  right: 0,
                                  left: 'auto',
                              }),
                    }}
                >
                    <Box sx={{ padding: '5px 10px 5px 10px' }}>
                        <Typography variant="h6" sx={{ marginBottom: '0px' }}>
                            {t('columns')}
                        </Typography>
                    </Box>

                    {/* Render draggable or regular list */}
                    {renderDraggableItems}

                    {/* Save/Reset buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            padding: '10px',
                            borderTop: '1px solid #e0e0e0',
                        }}
                    >
                        <Button
                            onClick={() => {
                                handleSave()
                                setIsFilterOpen(false)
                            }}
                            variant="contained"
                            size="small"
                            sx={{ flex: 1 }}
                        >
                            {t('save')}
                        </Button>
                        <Button
                            onClick={handleOnResetList}
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1 }}
                        >
                            {t('reset')}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    )
}

export default ColumnOrderList
